import { useNavigate } from "react-router-dom"
import { useMemo, useState, type ReactNode } from "react"
import { CircleDot, Goal, Settings, Shield, Trophy } from "lucide-react"
import {
  buildHomeLeaderValueText,
  formatHomePnlValue,
  resolveHomeSummaryTotalPnl,
} from "@sportex100/shared/utils/homePnlParity.ts"
import {
  isBaseballTradingCompetition,
  isFootballTradingCompetition,
  isHockeyTradingCompetition,
  isSoccerTradingCompetition,
} from "@sportex100/shared/utils/competitionIdentity.ts"
import type { PoolMembership } from "../../types/pools"
import type { HomeSummaryPoolEntry } from "../../hooks/useHomePoolSummary"
import { SegmentedFilter, StatusMessage } from "../ui"
import { useLocalization } from "../../lib/i18n"

interface HomePoolListProps {
  pools: PoolMembership[]
  summaryByPoolCode?: Map<string, HomeSummaryPoolEntry>
  joinPoolCode?: string
  isJoiningPool?: boolean
  joinPoolFeedback?: string | null
  joinPoolFeedbackKind?: "success" | "error" | "info" | null
  onJoinPoolCodeChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onJoinPoolSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
  onCreatePool?: () => void
  activePoolCode: string | undefined
  currentTraderCode?: string | null
  isLoading: boolean
  isError: boolean
  onSelectPool: (poolCode: string) => void
  onManagePool: (pool: PoolMembership) => void
  onMarkRead?: (poolCode: string, lastSeenRank?: number | null) => Promise<void> | void
  tutorialSlot?: ReactNode
  pulseSlot?: ReactNode
}

function getPoolSportIcon(competitionCode: string | null | undefined) {
  if (isSoccerTradingCompetition(competitionCode)) return Goal
  if (isHockeyTradingCompetition(competitionCode)) return CircleDot
  if (isFootballTradingCompetition(competitionCode)) return Shield
  if (isBaseballTradingCompetition(competitionCode)) return CircleDot
  return Trophy
}

export function HomePoolList({
  pools,
  summaryByPoolCode = new Map<string, HomeSummaryPoolEntry>(),
  activePoolCode,
  currentTraderCode = null,
  isLoading,
  isError,
  onSelectPool,
  onManagePool,
  onMarkRead,
  tutorialSlot = null,
  pulseSlot = null,
}: HomePoolListProps) {
  const navigate = useNavigate()
  const { t } = useLocalization()
  const [filter, setFilter] = useState<"ACTIVE" | "ALL">("ACTIVE")
  const statusTone: Record<PoolMembership["pool_status"], string> = {
    PRE: "bg-slate-500/10 text-slate-200 border-slate-500/40",
    ACTIVE: "bg-emerald-500/10 text-emerald-200 border-emerald-500/40",
    COMPLETED: "bg-brand/10 text-brand border-brand/40",
  }
  const filteredPools = useMemo(() => {
    const isCurrentPool = (pool: PoolMembership) =>
      pool.pool_status === "ACTIVE" || pool.pool_status === "PRE"

    if (filter === "ALL") return pools.filter((pool) => !isCurrentPool(pool))
    return pools.filter(isCurrentPool)
  }, [filter, pools])

  return (
    <div className="min-w-0 space-y-4 overflow-x-hidden">
      {isLoading && <StatusMessage>{t("home.feed.loading")}</StatusMessage>}

      {isError && !isLoading && (
        <StatusMessage variant="error">{t("home.feed.error")}</StatusMessage>
      )}

      {!isLoading && !isError && pools.length > 0 && (
        <>
          <div className="flex min-w-0 flex-col gap-3">
            <SegmentedFilter
              value={filter}
              onChange={setFilter}
              options={[
                { value: "ACTIVE", label: t("home.filters.current") },
                { value: "ALL", label: t("home.filters.archived") },
              ]}
              ariaLabel="Pool status filter"
              className="w-full"
            />
            {tutorialSlot}
            {pulseSlot}
          </div>

          {filteredPools.length === 0 ? (
            <StatusMessage>{t("home.empty.filteredTitle")}</StatusMessage>
          ) : (
            <div className="grid min-w-0 gap-4 md:grid-cols-2">
              {filteredPools.map((pool) => {
                const isActive = activePoolCode === pool.pool_code
                const isPending = pool.membership_status === "PENDING"
                const isCompleted = pool.pool_status === "COMPLETED"
                const summary = summaryByPoolCode.get(pool.pool_code) ?? null
                const totalPnl = resolveHomeSummaryTotalPnl(summary, pool.points_balance)
                const rankNow = summary?.momentum.rank_now ?? null
                const openOrdersLabel =
                  pool.open_orders_count <= 0
                    ? t("home.pool.noOrders")
                    : pool.open_orders_count === 1
                      ? t("home.pool.oneOrder")
                      : t("home.pool.manyOrders", { count: pool.open_orders_count })
                const pnlTone =
                  (totalPnl ?? 0) > 0
                    ? "text-success"
                    : (totalPnl ?? 0) < 0
                      ? "text-danger"
                      : "text-foreground"
                const leaderTraderCode = summary?.competitive_snapshot?.leader?.trader_code ?? null
                const leaderTotalPnl = summary?.competitive_snapshot?.leader?.points ?? null
                const leaderValueText = buildHomeLeaderValueText({
                  leaderTraderCode,
                  leaderTotalPnl,
                  currentTraderCode,
                  currentTotalPnl: totalPnl,
                  compact: true,
                  labels: {
                    empty: t("home.pool.leader.empty"),
                  },
                })
                const topClimberCode = summary?.competitive_snapshot?.top_climber_24h?.trader_code ?? null
                const topClimberDelta = summary?.competitive_snapshot?.top_climber_24h?.rank_delta_24h ?? 0
                const isCurrentUserTopClimber =
                  !!topClimberCode &&
                  !!currentTraderCode &&
                  topClimberCode.trim().toUpperCase() === currentTraderCode.trim().toUpperCase()
                const topClimberValueText =
                  topClimberCode && topClimberDelta > 0
                    ? isCurrentUserTopClimber
                      ? t("home.pool.youToday", { delta: topClimberDelta })
                      : t("home.pool.traderToday", { traderCode: topClimberCode, delta: topClimberDelta })
                    : t("home.pool.stable")
                const PoolSportIcon = getPoolSportIcon(pool.competition_code)

                const handleSelect = () => {
                  onSelectPool(pool.pool_code)
                  void onMarkRead?.(pool.pool_code, summary?.momentum.rank_now)
                }

                const handlePortfolio = (event: React.MouseEvent) => {
                  event.stopPropagation()
                  if (isPending) return
                  handleSelect()
                  navigate("/portfolio")
                }

                const handleStandings = (event: React.MouseEvent) => {
                  event.stopPropagation()
                  if (isPending) return
                  handleSelect()
                  navigate("/standings")
                }

                return (
                  <article
                    key={pool.pool_code}
                    className={`relative min-w-0 overflow-hidden rounded-lg border p-4 transition-all duration-200 ease-out ${
                      isPending
                        ? isActive
                          ? "cursor-pointer border-2 border-brand bg-amber-500/10 shadow-glow hover:translate-y-[-1px]"
                          : "cursor-pointer border-2 border-amber-500/50 bg-amber-500/5 hover:translate-y-[-1px] hover:border-amber-400/70"
                        : `cursor-pointer hover:translate-y-[-1px] ${
                            isActive
                              ? "border-2 border-brand bg-background-card shadow-glow"
                              : "border-2 border-border bg-background-card hover:border-brand/60 hover:bg-background-surface"
                          }`
                    }`}
                    onClick={handleSelect}
                  >
                    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                      <span className="absolute -left-7 -top-6 h-[140px] w-[88%] rotate-[-17deg] rounded-full bg-[linear-gradient(110deg,rgba(255,255,255,0),rgba(255,255,255,0.08),rgba(255,255,255,0))] opacity-80" />
                      <span className="absolute -right-10 top-12 h-28 w-[92%] rotate-[-17deg] rounded-full bg-[linear-gradient(110deg,rgba(255,255,255,0),rgba(255,255,255,0.05),rgba(255,255,255,0))] opacity-70" />
                      <span className="absolute -bottom-4 left-5 h-24 w-[72%] rotate-[11deg] rounded-full bg-[linear-gradient(110deg,rgba(255,255,255,0),rgba(255,255,255,0.035),rgba(255,255,255,0))] opacity-60" />
                    </div>

                    <div className="relative z-10">
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <h3 className="truncate font-display text-title-sm font-bold uppercase text-foreground md:text-title">
                            {pool.pool_name}
                          </h3>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {pool.is_admin && !isCompleted ? (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation()
                                handleSelect()
                                onManagePool(pool)
                              }}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-base border border-border text-brand transition hover:border-brand/60 hover:bg-background-raised"
                              title={t("poolWizard.title.manage")}
                            >
                              <Settings className="h-4 w-4" aria-hidden="true" />
                            </button>
                          ) : null}
                          <span
                            className={`inline-flex items-center rounded-base border px-2 py-1 font-display text-[11px] font-semibold uppercase tracking-wide ${statusTone[pool.pool_status]}`}
                          >
                            {pool.pool_status === "PRE"
                              ? t("home.pool.status.preseason")
                              : pool.pool_status === "COMPLETED"
                                ? t("home.pool.status.completed")
                                : t("home.pool.status.active")}
                          </span>
                        </div>
                      </div>

                      <div className="mb-3 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 font-mono text-body-sm text-foreground-muted">
                        <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
                          <PoolSportIcon className="h-3.5 w-3.5 text-foreground-muted" aria-hidden="true" />
                        </span>
                        <span className="min-w-0 truncate">{pool.competition_name}</span>
                        <span aria-hidden="true">•</span>
                        <span>{pool.season_code}</span>
                        <span aria-hidden="true">|</span>
                        <button
                          type="button"
                          onClick={handlePortfolio}
                          disabled={isPending || pool.open_orders_count <= 0}
                          className={`font-semibold transition ${
                            isPending || pool.open_orders_count <= 0
                              ? "text-foreground-muted/60"
                              : "text-brand hover:text-brand-hover"
                          }`}
                        >
                          {openOrdersLabel}
                        </button>
                      </div>

                      {isPending ? (
                        <p className="mb-3 text-sm text-amber-200">{t("home.pool.pendingApproval")}</p>
                      ) : null}

                      {summary ? (
                        <>
                          <div className="mt-3 mb-2 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-display text-label-sm font-bold uppercase text-foreground-muted">
                                {t("home.pool.stat.pnl")}
                              </p>
                              <p className={`mt-0.5 truncate font-mono text-3xl font-black tabular-nums ${pnlTone}`}>
                                {formatHomePnlValue(totalPnl)}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={handleStandings}
                              disabled={isPending}
                              className="shrink-0 text-right transition disabled:cursor-default disabled:opacity-60 enabled:hover:text-brand"
                            >
                              <span className="block font-display text-label-sm font-bold uppercase text-foreground-muted">
                                {t("home.pool.stat.ranking")}
                              </span>
                              <span className="mt-0.5 flex items-center justify-end gap-2">
                                <span className="font-mono text-3xl font-black tabular-nums text-foreground">
                                  {rankNow != null ? `#${rankNow}` : "—"}
                                </span>
                              </span>
                            </button>
                          </div>

                          <div className="mb-2 h-px bg-white/10" />

                          <div className="space-y-2">
                            <div className="grid min-h-[38px] grid-cols-[72px_minmax(0,1fr)] items-start gap-3">
                              <p className="font-display text-label-sm font-semibold uppercase text-foreground-muted">
                                {t("home.pool.leading")}
                              </p>
                              <p
                                className={`min-w-0 text-right font-mono text-body-sm font-semibold ${
                                  !leaderTraderCode || leaderTotalPnl == null ? "text-foreground-muted" : "text-foreground"
                                }`}
                              >
                                {leaderValueText}
                              </p>
                            </div>
                            <div className="grid min-h-[38px] grid-cols-[72px_minmax(0,1fr)] items-start gap-3">
                              <p className="font-display text-label-sm font-semibold uppercase text-foreground-muted">
                                {t("home.pool.rising")}
                              </p>
                              <p
                                className={`min-w-0 text-right font-mono text-body-sm font-semibold ${
                                  topClimberCode && topClimberDelta > 0 ? "text-success" : "text-foreground-muted"
                                }`}
                              >
                                {topClimberValueText}
                              </p>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="mt-3 mb-2 border-b border-white/10 pb-3">
                          <p className="font-display text-label-sm font-bold uppercase text-foreground-muted">
                            {t("home.pool.stat.pnl")}
                          </p>
                          <p className={`mt-0.5 truncate font-mono text-3xl font-black tabular-nums ${pnlTone}`}>
                            {formatHomePnlValue(totalPnl)}
                          </p>
                        </div>
                      )}

                      {isPending && (
                        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-center">
                          <p className="text-sm font-medium text-amber-200">{t("home.pool.awaitingApproval")}</p>
                          <p className="mt-1 text-xs text-foreground-muted">
                            You&apos;ll have access once the pool admin approves your request.
                          </p>
                        </div>
                      )}
                    </div>
                  </article>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}
